import React from 'react';
import { Box, TableRow, TableCell, Typography, IconButton, Tooltip } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MatchEventRow from './MatchEventRow';
import MultiDatasetMatchEventRow from './MultiDatasetMatchEventRow';

/**
 * GroupMatchEventRow - Renders a group_match event with expandable children
 *
 * Group matches can contain multiple individual match events (one per person detected).
 * This component displays:
 * - Group summary (match count, detected face count)
 * - Expand/collapse to show individual match children
 * - Person names preview
 *
 * Optimized with React.memo to prevent unnecessary re-renders
 */
const GroupMatchEventRow = React.memo(function GroupMatchEventRow({
  event,
  childEvents = [],
  expanded = false,
  onToggleExpand,
  isChild = false,
  address,
  getAddress,
  onLocationClick
}) {
  const hasValidMetadata = event.metadata &&
    typeof event.metadata === 'object' &&
    !event.metadata.S &&
    !event.metadata.M &&
    !event.metadata.NULL;

  if (!hasValidMetadata) {
    return null;
  }

  const personNames = event.metadata?.personNames || [];
  const matchCount = event.metadata?.matchCount || personNames.length;
  const detectedFaceCount = event.metadata?.detectedFaceCount || 0;
  const username = event.userHandle || event.userEmail || event.userId;
  const timestamp = new Date(event.timestamp || event.createdAt);
  const totalChildren = childEvents.length;

  // Get color for processing elapsed time
  const getElapsedTimeColor = (ms) => {
    if (ms < 500) return '#4caf50'; // Green
    if (ms < 750) return '#ff9800'; // Amber
    if (ms >= 1500) return '#f44336'; // Red
    return '#ff9800'; // Amber for 750-1499ms range
  };

  // Background color based on whether it's a child event
  const backgroundColor = isChild ? '#f5f9fd' : '#e3f2fd';
  const borderColor = isChild ? '4px solid #90caf9' : '4px solid #1976d2';

  return (
    <>
      {/* Group Match Header Row */}
      <TableRow
        hover
        onClick={totalChildren > 0 ? onToggleExpand : undefined}
        sx={{
          backgroundColor,
          borderLeft: borderColor,
          cursor: totalChildren > 0 ? 'pointer' : 'default',
          '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' },
          '&:hover': {
            backgroundColor,
            filter: totalChildren > 0 ? 'brightness(0.98)' : 'none'
          }
        }}
      >
        {/* Event Type Icon */}
        <TableCell align="center" sx={{ width: 40 }}>
          <Tooltip title="Group Match">
            <GroupIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          </Tooltip>
        </TableCell>

        {/* Expand/Collapse Button */}
        <TableCell align="center" sx={{ width: 40 }}>
          {totalChildren > 0 && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
              sx={{ p: 0.5 }}
            >
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </TableCell>

      {/* Event Details */}
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Group photo - {matchCount} match{matchCount !== 1 ? 'es' : ''} in {detectedFaceCount} face{detectedFaceCount !== 1 ? 's' : ''}
              </Typography>
              {personNames.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {personNames.slice(0, 3).join(', ')}
                  {personNames.length > 3 && ` +${personNames.length - 3} more`}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block">
                by {username}
              </Typography>
            </Box>

            {/* Child Match Count */}
            {totalChildren > 0 && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {totalChildren} individual match{totalChildren !== 1 ? 'es' : ''}
                </Typography>
              </Box>
            )}
          </Box>
        </TableCell>

        {/* Timestamp */}
        <TableCell sx={{ width: 140 }}>
          <Typography variant="caption">
            {timestamp.toLocaleString()}
          </Typography>
          {event.metadata?.processingElapsedMs !== undefined && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: '0.7rem',
                color: getElapsedTimeColor(event.metadata.processingElapsedMs),
                fontWeight: 500
              }}
            >
              +{event.metadata.processingElapsedMs}ms
            </Typography>
          )}
        </TableCell>

        {/* User Account */}
        <TableCell sx={{ width: 120 }}>
          <Typography variant="caption" color="text.secondary">
            {event.userHandle || event.userEmail || event.userId || '—'}
          </Typography>
        </TableCell>

        {/* Location */}
        <TableCell>
          {event.location ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onLocationClick(event);
                }}
                sx={{ p: 0.5 }}
              >
                <LocationOnIcon fontSize="small" color="primary" />
              </IconButton>
              <Typography variant="caption">
                {address || `${event.location.latitude.toFixed(4)}, ${event.location.longitude.toFixed(4)}`}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption">N/A</Typography>
          )}
        </TableCell>
      </TableRow>

      {/* Child Match Rows (only render when expanded) */}
      {expanded && childEvents.map((childEvent) => {
        if (childEvent.eventType === 'multi_dataset_match') {
          return (
            <MultiDatasetMatchEventRow
              key={childEvent.eventId}
              event={childEvent}
              isChild={true}
              address={getAddress(childEvent)}
              onLocationClick={onLocationClick}
            />
          );
        }
        return (
          <MatchEventRow
            key={childEvent.eventId}
            event={childEvent}
            isChild={true}
            address={getAddress(childEvent)}
            onLocationClick={onLocationClick}
          />
        );
      })}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these changed
  return (
    prevProps.event.eventId === nextProps.event.eventId &&
    prevProps.expanded === nextProps.expanded &&
    prevProps.childEvents?.length === nextProps.childEvents?.length &&
    prevProps.isChild === nextProps.isChild &&
    prevProps.address === nextProps.address
  );
});

export default GroupMatchEventRow;
